import { useLocale } from 'locale';
import React from 'react';
import * as Styled from './styled';
import { PoiId } from 'utils/entities';
import PhoneIcon from 'assets/svg/phone-new.svg';
import { createPhoneURI } from 'hooks/useDisplayPhone';
import { IContactPageSubmitVariables } from 'store/sagas/apiService/types';
import { PrivateContactProps } from 'components/contact/types';
import UserPlaceholder from 'assets/svg/profile-new.svg';
import { ContactAgentModalSource, ContactModalOpenMeta } from 'store/state/app';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { Button } from 'ds/components/button';
import MailIcon from 'assets/svg/send.svg';
import ContactButtons from 'components/contact-buttons';
import useWhatsAppHref from 'hooks/useWhatsAppHref';
import _ from 'lodash';
import ContactsWithWhatsAppExpandable from 'components/contact/whatsapp-expandable';

interface GalleryPrivateStickyProps extends PrivateContactProps {
  id: PoiId;
  onSetContactModalPoiId: (id: PoiId, meta: ContactModalOpenMeta) => void;
  onCallClickEvent?: (isVisible: boolean, formValues: IContactPageSubmitVariables, source?: string) => void;
}

export const GalleryPrivateSticky: React.FC<GalleryPrivateStickyProps> = (props) => {
  const {
    contactValues,
    isContacted,
    name,
    phone,
    poiType,
    id,
    whatsappNumber,
    poi,
    leadApi3Type,
    user,
    onCallClickEvent,
    onSetContactModalPoiId,
  } = props;

  const { t } = useLocale();
  const screenBreakpoint = useScreenBreakpoint();
  const isMobile = screenBreakpoint < 3;
  const [ phoneNumberIsVisible, setPhoneNumberIsVisible ] = React.useState(false);
  const validPhoneNumber = Boolean(whatsappNumber);
  const whatsAppHref = useWhatsAppHref(whatsappNumber, name, id);


  const handleModalOpen = () => {
    onSetContactModalPoiId(id, { source: ContactAgentModalSource.FullScreenGallery });
  };

  const handlePhoneSubmit = (e: React.MouseEvent<HTMLElement>) => {
    onCallClickEvent(isMobile || phoneNumberIsVisible, { ...contactValues, id, type: poiType }, ContactAgentModalSource.FullScreenGallery);

    if (!phoneNumberIsVisible && !isMobile) {
      e.preventDefault();
      setPhoneNumberIsVisible(true);
    }
  };

  if (isMobile) {
    return (
      <Styled.Root data-auto="private-gallery-sticky">
        <Styled.ButtonWrapper>
          <ContactsWithWhatsAppExpandable
            poi={poi}
            user={user}
            leadApi3Type={leadApi3Type}
            userPhone={contactValues.phone}
            whatsAppHref={whatsAppHref}
            whatsappNumber={whatsappNumber}
            validPhoneNumber={validPhoneNumber}
          >
            <Styled.PhoneButton
              data-auto="phone-number-button"
              size="extraLarge"
              weight="medium"
              onClick={handlePhoneSubmit}
              href={createPhoneURI(phone)}
            >
              <PhoneIcon />
              {phoneNumberIsVisible ? phone : (isMobile ? t('contactAgentModal.callButton') : t('contactAgentModal.callButton.desktop'))}
            </Styled.PhoneButton>
          </ContactsWithWhatsAppExpandable>
          {validPhoneNumber ? null : (
            <>
              {isContacted ? (
                <Button isInitialIconColor disabled data-auto="phone-private-inner-number-button-modal-disabled" size="extraLarge" mode="ghost">
                  {t('contactAgentModal.button.disabled.modeB.static')}
                </Button>
              ) : (
                <Button isInitialIconColor onClick={handleModalOpen} data-auto="phone-private-inner-number-button-modal" size="extraLarge" mode="ghost">
                  <MailIcon width={24} height={24} />
                  {t('contactAgentModal.open')}
                </Button>
              )}
            </>
          )}
        </Styled.ButtonWrapper>
      </Styled.Root>
    );
  }

  return (
    <Styled.Root data-auto="private-gallery-sticky">
      <Styled.InfoBox>
        <UserPlaceholder width={56} height={56} />
        <Styled.UserName weight="bold">{name}</Styled.UserName>
      </Styled.InfoBox>
      <ContactButtons
        phone={phone}
        onShowPhone={handlePhoneSubmit}
        isContacted={isContacted}
        handleModalOpen={handleModalOpen}
        showPhone={phoneNumberIsVisible}
        dataAttributePrefix="private-gallery-sticky"
        source={ContactAgentModalSource.FullScreenGallery}
      />
    </Styled.Root>
  );
};
